import { useQueries } from "@tanstack/react-query";
import { observer } from "mobx-react";
import { FormattedMessage } from "react-intl";
import { Linking } from "react-native";
import { Spacer } from "~/components/Spacer";
import { AppointmentTypeCard } from "~/components/V2/AppointmentTypeCard";
import { ButtonV2 } from "~/components/V2/Button";
import { ErrorViewV2 } from "~/components/V2/ErrorViewV2";
import { SpinnerV2 } from "~/components/V2/SpinnerV2";
import { BodyText } from "~/components/V2/Text/BodyText";
import { BrandText } from "~/components/V2/Text/BrandText";
import { View } from "~/components/V2/View";
import { useClinicPhoneNumber } from "~/hooks/useClinicPhoneNumber";
import { useStore } from "~/mobx/utils/useStore";
import { styleConstantsV2 } from "~/style/styleConstantsV2";
import { assert } from "~/utils/assert";
import { getPhoneNumberWithCountryCode } from "~/utils/getPhoneNumberWithCountryCode";
import { bookingDataLayerPush } from "~/utils/logBookingEvent";
import { useProgressBarStep } from "./useProgressBarStep";
import { withBookingData } from "./withBookingData";

const SelectAppointmentTypeScreen = observer(
  function SelectAppointmentTypeScreen() {
    const store = useStore();
    const { clinicPhoneNumber, clinicPhoneNumberCountryCode } =
      useClinicPhoneNumber();

    useProgressBarStep(3);

    const selectedClinicId = store.bookingStore.clinicId;
    const selectedSpeciesId = store.bookingStore.patientSpeciesId;

    assert(selectedClinicId, "Clinic ID is not set, can not fetch reasons");

    const [appointmentReasonsQuery, clinicQuery] = useQueries({
      queries: [
        {
          queryKey: [
            "appointmentReasons",
            { clinicId: selectedClinicId, speciesId: selectedSpeciesId },
          ],
          queryFn: async () => {
            const reasons =
              await store.appointmentReasonStore.getAppointmentReasonList(
                selectedClinicId,
                selectedSpeciesId,
              );

            return reasons;
          },
        },
        {
          queryKey: ["clinic", selectedClinicId],
          queryFn: async () => {
            const clinic = await store.clinicsStore.getClinic(selectedClinicId);
            return clinic;
          },
        },
      ],
    });

    const website = clinicQuery.data?.website;

    if (appointmentReasonsQuery.isLoading) {
      return (
        <View flex colorWhite>
          <View padding32 centerContent>
            <SpinnerV2 />
          </View>
        </View>
      );
    }

    if (appointmentReasonsQuery.isError) {
      return (
        <View flex colorWhite>
          <View centerContent paddingHorizontal24>
            <ErrorViewV2
              error={appointmentReasonsQuery.error}
              title={intl.formatMessage({
                id: "m4dnTD",
                defaultMessage: "Something went wrong",
                description:
                  "Error message title when fetching clinic list fails",
              })}
            />
            <ButtonV2
              onPress={appointmentReasonsQuery.refetch}
              title={intl.formatMessage({
                id: "ghblYR",
                defaultMessage: "Try Again!",
                description: "Button to retry fetching clinic list",
              })}
            />
          </View>
        </View>
      );
    }

    const appointmentTypes = appointmentReasonsQuery.data;

    assert(appointmentTypes, "Appointment types do not exist");

    return (
      <View paddingHorizontal16 flex colorWhite>
        <View paddingVertical40 alignItemsCenter>
          <BrandText sizeH1 weightExtraBold colorBlack>
            <FormattedMessage
              id="OZc0Dg"
              defaultMessage="Select service"
              description="Title on the select appointment type screen in booking flow"
            />
          </BrandText>
        </View>

        <View style={{ gap: styleConstantsV2.spacing16 }}>
          {appointmentTypes.map((appointmentType) => {
            return (
              <AppointmentTypeCard
                key={appointmentType.id}
                appointmentType={appointmentType}
              />
            );
          })}
        </View>
        <Spacer extraExtraLarge />

        {!!clinicPhoneNumber && !!clinicPhoneNumberCountryCode && (
          <View
            style={{
              paddingBottom: styleConstantsV2.spacing64,
              paddingHorizontal: styleConstantsV2.spacing24,
            }}
          >
            <BodyText alignCenter sizeExtraLarge weightRegular colorBlack>
              <FormattedMessage
                defaultMessage="If you do not find the service you are looking for, please contact us at <clinicPhoneNumberText>{clinicPhoneNumber}</clinicPhoneNumberText>."
                id="oIhAbm"
                description="Text informing the user to contact the clinic if they don't have BankID"
                values={{
                  clinicPhoneNumber: clinicPhoneNumber,
                  clinicPhoneNumberText: (chunks) => {
                    return (
                      <BodyText
                        alignCenter
                        sizeExtraLarge
                        weightRegular
                        colorPrimary
                        underline
                        onPress={() => {
                          // Log the event to the booking data layer
                          bookingDataLayerPush({
                            event: "call_clinic_phone_web_app",
                            ga_action: "call_clinic_phone",
                            ga_label: "SelectAppointmentTypeScreen",
                          });

                          Linking.openURL(
                            `tel:${getPhoneNumberWithCountryCode(clinicPhoneNumberCountryCode, clinicPhoneNumber)}`,
                          );
                        }}
                      >
                        {chunks}
                      </BodyText>
                    );
                  },
                }}
              />
            </BodyText>

            {website && (
              <BodyText alignCenter sizeExtraLarge weightRegular colorBlack>
                <FormattedMessage
                  defaultMessage='See pricing information on our website under <pricesLink>"Prices"</pricesLink>.'
                  id="KbN9l8"
                  description="Text informing the user to check the clinic website for pricing information"
                  values={{
                    pricesLink: (chunks) => {
                      return (
                        <BodyText
                          alignCenter
                          sizeExtraLarge
                          weightRegular
                          colorPrimary
                          underline
                          onPress={() => {
                            // Log the event to the booking data layer
                            bookingDataLayerPush({
                              event: "view_prices_web_app",
                              ga_action: "view_prices",
                              ga_label: "SelectAppointmentTypeScreen",
                            });

                            Linking.openURL(`${website}priser`);
                          }}
                        >
                          {chunks}
                        </BodyText>
                      );
                    },
                  }}
                />
              </BodyText>
            )}
          </View>
        )}
      </View>
    );
  },
);

// If any required data from earlier in the booking process is missing, redirect to the first step
export const SelectAppointmentTypeScreenWithBookingData = withBookingData(
  SelectAppointmentTypeScreen,
  ["clinicId", "patientId"],
);
